/* eslint-disable simple-import-sort/imports */
import React, { useId, useMemo } from 'react';
import { Loader2 } from 'lucide-react';

import { MultiSelect } from '~/shadcn/components/ui/MultiSelect';
import { useBuyerOptions } from '../queries/PurchaseOrderQueries';

/**
 * Renders a buyers picker multi-select component.
 *
 * @param {Object} props Component props.
 * @param {string|number|Object|Array<string|number|Object>} [props.value] Selected buyer ID(s), name(s), or buyer object(s).
 * @param {Function} [props.onValueChange] Change handler invoked with the selected buyer ID(s).
 * @param {string} [props.label='Buyer(s)'] Visible label rendered above the picker.
 * @param {boolean} [props.showLabel=true] Shows the label when true, hides it when false.
 * @returns {JSX.Element} Buyers picker component.
 */
export function BuyersPicker({ label = 'Buyer(s)', onValueChange, showLabel = true, value }) {
    const labelId = useId();
    const { data: buyerOptions = [], isFetching, isLoading } = useBuyerOptions();

    // Extract ID from value (handles ID, name, or buyer object)
    // Note: Name-based lookup is not supported when using useBuyerOptions
    // since we don't have access to raw buyer objects for name matching
    // Returns the ID as a number to match useBuyerOptions value type
    const extractId = (val) => {
        if (val == null) return null;
        if (typeof val === 'object' && val.id != null) {
            return Number(val.id);
        }
        const numVal = Number(val);
        // If it's a valid number, return it
        if (!Number.isNaN(numVal) && Number.isFinite(numVal)) {
            return numVal;
        }
        // Name-based lookup not available with useBuyerOptions
        return null;
    };

    // Derive selected value as array of IDs (for MultiSelect)
    // Must match the option value type (number) for strict equality comparison
    const selectedValue = useMemo(() => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.map(extractId).filter((id) => id != null);
        }
        const id = extractId(value);
        return id != null ? [id] : [];
    }, [value]);

    // Handle multi-select change - return array of buyer IDs
    const handleValueChange = (newValues) => {
        if (onValueChange) {
            onValueChange(newValues);
        }
    };

    // Show loading spinner when fetching and no data
    const showLoading = isLoading || (isFetching && buyerOptions.length === 0);

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span
                        id={labelId}
                        className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading buyers…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span
                    id={labelId}
                    className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                    {label}
                </span>
            )}
            <MultiSelect
                key={`buyers-${selectedValue.slice().sort().join('|')}-${buyerOptions.length}`}
                aria-labelledby={labelId}
                options={buyerOptions}
                defaultValue={selectedValue}
                onValueChange={handleValueChange}
                placeholder="Select buyers"
            />
        </div>
    );
}

BuyersPicker.displayName = 'BuyersPicker';
